import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, FormGroup, ListGroup } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { ReportsDataService } from '../../Services/ReportsDataService';
import { ReportsConfigurationDataService } from '../../Services/ReportsConfigurationDataService';
import { powerBiConfig } from '../../apiConfig';

const ReportsManagement = () => {
    const { instance, accounts, inProgress } = useMsal();
    const [availableWorkspaces, setAvailableWorkspaces] = useState([]);
    const [selectedWorkspace, setSelectedWorkspace] = useState('');
    const [reports, setReports] = useState([]); // Will hold the merged list of available and selected reports
    const [availableReports, setAvailableReports] = useState([]); // All available reports from the workspace
    const [savedReports, setSavedReports] = useState([]); // Reports already selected by the user

    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchAvailableWorkspaces(); // Fetch available workspaces first
            fetchSavedReports(); // Then fetch saved reports
        }
    }, [accounts, inProgress]);

    useEffect(() => {
        if (selectedWorkspace != '') {
            fetchAvailableReports(selectedWorkspace); // Fetch available reports first
        }else{
            setReports([]);
        }
    }, [selectedWorkspace]);

    const fetchAvailableWorkspaces = async () => {
        try {
            const data = await ReportsDataService.getWorkspaces();
            setAvailableWorkspaces(data);
        } catch (error) {
            console.error('Error fetching available reports from workspace:', error);
        }
    };

    // Fetch reports that the user has saved (already selected)
    const fetchSavedReports = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const data = await ReportsConfigurationDataService.getAllReports(response.accessToken);
            setSavedReports(data);
        } catch (error) {
            console.error('Failed to fetch saved reports:', error);
        }
    };

    // Fetch all available reports from the workspace
    const fetchAvailableReports = async () => {
        try {
            const data = await ReportsDataService.getReportsFromWorkspace(selectedWorkspace);
            setAvailableReports(data);
        } catch (error) {
            console.error('Error fetching available reports from workspace:', error);
        }
    };

    // Merge available reports with saved reports to mark the selected ones
    useEffect(() => {
        if (availableReports.length > 0) {
            const mergedReports = availableReports.map(availableReport => {
                const savedReport = savedReports.find(r => r.reportId === availableReport.id);
                return {
                    ...availableReport,
                    reportId: availableReport.id,
                    reportName: availableReport.name,
                    workspaceId: selectedWorkspace,
                    isSelected: savedReport ? savedReport.isSelected : false // Pre-check the saved reports
                };
            });
            setReports(mergedReports);
        }
    }, [availableReports, savedReports]);    

    const handleWorkspaceChange = (e) => {
        setSelectedWorkspace(e.target.value)
    }

    const handleSelectionChange = (index) => {
        const updatedReports = [...reports];
        updatedReports[index].isSelected = !updatedReports[index].isSelected;
        setReports(updatedReports);
    };

    const handleSaveReports = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
    
            // Send the entire reports list, not just selected ones
            await ReportsConfigurationDataService.updateReports(reports, response.accessToken);
            alert('Reports updated successfully.');
            fetchSavedReports();
        } catch (error) {
            console.error('Failed to update reports:', error);
        }
    };
    

    return (
        <Container>
            <Row className='justify-content-center'>
                <Col xl={9}>
                    <h2>Reports Management</h2>
                    <h5 className='mt-4'>Current report selection</h5>
                    <ListGroup>
                        {savedReports.filter(x => x.isSelected).map((report, index) => (
                            <ListGroup.Item>{report.reportName}</ListGroup.Item>
                        ))}
                    </ListGroup>
                    <h5 className='mt-4'>Update report selection</h5>
                    <Form>
                        <FormGroup className='mb-3'>
                            <Form.Label>Available Workspaces</Form.Label>
                            <Form.Select onChange={handleWorkspaceChange} value={selectedWorkspace}>
                                <option value=''>Select a workspace to view the reports</option>
                                {availableWorkspaces.map((workspace, index) => (
                                    <option key={index} value={workspace.id}>{workspace.name}</option>
                                ))}
                            </Form.Select>
                        </FormGroup>
                        {reports.length > 0 ? (
                            <>
                            <Form.Label>Available Reports</Form.Label>
                            {
                                reports.map((report, index) => (
                                    <Form.Check
                                        key={report.reportId}
                                        type="checkbox"
                                        label={report.reportName}
                                        checked={report.isSelected}
                                        onChange={() => handleSelectionChange(index)}
                                    />
                                ))
                            }
                            </>
                        ) : (
                            <p>No reports available.</p>
                        )}
                    </Form>
                    <div className='d-flex justify-content-end mt-4'>
                        <Button className="px-4" onClick={handleSaveReports}>Save</Button>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default ReportsManagement;
